import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  getIntegration,
  saveEntityTwin,
} from "../integration/integration-service";
import useApiCall from "../common/api-call";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import LoadingSpinner from "../common/loading";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";

const UpsertDeviceDialog = ({
  open,
  onClose,
  onBeforeSave,
  onSave,
  currentCanvasId = null,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [devices, setDevices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [optionsList, setOptionsList] = useState([]);
  const [twinTags, setTwinTags] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});

  const { apiCall } = useApiCall();
  const [userInfo] = useRecoilState(userInfoState);
  const [loading, setLoading] = useState(false);
  const { successToast, errorToast } = useToast();
  const [loadingSave, setLoadingSave] = useState(false);
  const { ws, sendAndWaitWs, sendAndForgetWs } = useWebSocket();

  const [meta, setMeta] = useState({ twinNames: {} });

  const filterOptions = (options, { inputValue }) => {
    const searchTerms = inputValue
      .toLowerCase()
      .split(" ")
      .filter((term) => term.length > 0);

    return options.filter((option) => {
      let title = option.name;
      let subtitle =
        option.type === "device" ? `Device - ${option.integration}` : "Group";

      const parenthesisMatch = option.name.match(/^(.*?)\s*\((.*)\)$/);
      if (parenthesisMatch && parenthesisMatch[1] && parenthesisMatch[2]) {
        title = parenthesisMatch[1].trim();
        subtitle = parenthesisMatch[2].trim();
      }

      const searchText = `${title} ${subtitle}`.toLowerCase();
      return searchTerms.every((term) => searchText.includes(term));
    });
  };

  useEffect(() => {
    if (open) {
      handleLoadIntegration();
    } else {
      setSelectedItem(null);
      setTwinTags([]);
      setSelectedOptions({});
      setOptionsList([]);
      setDevices([]);
      setGroups([]);
    }
  }, [open]);

  const handleLoadIntegration = async () => {
    setLoading(true);
    setDevices([]);
    setGroups([]);
    setOptionsList([]);
    try {
      const resp = await getIntegration(
        apiCall,
        userInfo.activeScope.id,
        "basic.device"
      );

      if (resp) {
        // Get all entities and groups from all enabled types
        const fetchedDevices = [];
        const fetchedGroups = [];

        console.log(resp);

        // Process entities from all enabled types
        if (resp.meta?.entityEnabled) {
          Object.entries(resp.meta.entityEnabled).forEach(([type, enabled]) => {
            if (enabled && resp.entities[type]) {
              fetchedDevices.push(...resp.entities[type]);
            }
          });
        }

        // Process groups from all enabled types
        if (resp.meta?.groupEnabled) {
          Object.entries(resp.meta.groupEnabled).forEach(([type, enabled]) => {
            if (enabled && resp.groups[type]) {
              fetchedGroups.push(...resp.groups[type]);
            }
          });
        }

        setDevices(fetchedDevices);
        setGroups(fetchedGroups);
        setMeta(resp.meta || { twinNames: {} });

        const combinedOptions = [
          ...fetchedDevices.map((device) => ({
            type: "device",
            id: device.id,
            name: device.name || "Unnamed Device",
            integration: device.integration,
            twinTags: device.twinTags || [],
            original: device,
          })),
          ...fetchedGroups.map((group) => ({
            type: "group",
            id: group.groupId,
            name: `${group.name} (Group)` || "Unnamed Group",
            integration: group.integration || "basic.device",
            twinTags: group.twinTags || [],
            original: group,
          })),
        ].sort((a, b) => a.name.localeCompare(b.name));
        setOptionsList(combinedOptions);
      } else {
        errorToast("Error loading devices and groups");
        console.log("Fetch integration error! Response was:", resp);
      }
    } catch (error) {
      errorToast("Error loading devices and groups");
      console.error("Error loading integration data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTwinName = (twinTag) => {
    return (meta?.twinNames && meta.twinNames[twinTag]) || twinTag;
  };

  const handleItemChange = (event, newValue) => {
    setSelectedItem(newValue);

    if (newValue) {
      if (newValue.type === "device" || newValue.type === "group") {
        const selectedDeviceData = newValue.original;
        if (selectedDeviceData) {
          setTwinTags(selectedDeviceData.twinTags || []);
          const initialOptions = {};
          (selectedDeviceData.twinTags || []).forEach((tag) => {
            initialOptions[tag] = false;
          });
          setSelectedOptions(initialOptions);
        } else {
          setTwinTags([]);
          setSelectedOptions({});
        }
      }
    } else {
      setTwinTags([]);
      setSelectedOptions({});
    }
  };

  const handleCheckboxChange = (event) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [event.target.name]: event.target.checked,
    };
    console.log(newSelectedOptions);
    setSelectedOptions(newSelectedOptions);
  };

  const handleClose = () => {
    if (loadingSave) {
      return;
    }
    onClose();
  };

  const handleAdd = async () => {
    if (!selectedItem) {
      errorToast("Please select a device or group.");
      return;
    }
    console.log(selectedItem);

    let integrationToSave = "";
    let idToSave = "";
    let nameToSave = "";
    let tagsToSave = [];
    let isEntity;

    const hasSelectedTags = Object.values(selectedOptions).some(
      (value) => value === true
    );
    if (!hasSelectedTags) {
      errorToast("Please select at least one UI function.");
      return;
    }
    tagsToSave = Object.entries(selectedOptions)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
    integrationToSave = selectedItem.integration;
    idToSave = selectedItem.id;
    nameToSave = selectedItem.name;
    isEntity = true;

    setLoadingSave(true);
    if (onBeforeSave) onBeforeSave();

    try {
      const resp = await saveEntityTwin(
        apiCall,
        userInfo.activeScope.id,
        currentCanvasId,
        integrationToSave,
        idToSave,
        nameToSave,
        selectedItem.original.type,
        tagsToSave,
        selectedItem.type === "group",
        true
      );

      if (resp) {
        successToast("Device card created successfully!");
        if (onSave) onSave(resp);
        handleClose();
      } else {
        errorToast("Error creating device card.");
      }
    } catch (error) {
      errorToast("Error creating device card.");
      console.error("Error saving twin:", error);
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Device Card</DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Autocomplete
            sx={{ mt: 2 }}
            options={optionsList}
            getOptionLabel={(option) => option?.name || ""}
            value={selectedItem}
            onChange={handleItemChange}
            isOptionEqualToValue={(option, value) =>
              option.type === value.type && option.id === value.id
            }
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField {...params} label="Select Device or Group" />
            )}
            ListboxProps={{
              style: {
                maxHeight: "250px",
                overflowX: "auto",
              },
              sx: {
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
              },
            }}
            componentsProps={{
              paper: {
                sx: {
                  "& .MuiAutocomplete-listbox": {
                    padding: 0,
                    "& .MuiAutocomplete-option": {
                      padding: "8px 10px",
                      minWidth: "fit-content",
                      borderBottom: "1px solid #eee",
                    },
                  },
                },
              },
            }}
            renderOption={(props, option) => {
              let title = option.name;
              let subtitle = option.type === "device" ? `Device` : "Group";

              const parenthesisMatch = option.name.match(/^(.*?)\s*\((.*)\)$/);
              if (
                parenthesisMatch &&
                parenthesisMatch[1] &&
                parenthesisMatch[2]
              ) {
                title = parenthesisMatch[1].trim();
                subtitle = parenthesisMatch[2].trim();
              }

              return (
                <li {...props} key={`${option.type}-${option.id}`}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Typography variant="body1" component="div" noWrap>
                      {title}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color="textSecondary"
                    >
                      {subtitle}
                    </Typography>
                  </div>
                </li>
              );
            }}
          />
        )}

        {selectedItem && (
          <FormGroup sx={{ mt: 3 }}>
            {twinTags.length > 0 ? (
              twinTags.map((tag) => (
                <FormControlLabel
                  key={tag}
                  control={
                    <Checkbox
                      checked={selectedOptions[tag] || false}
                      onChange={handleCheckboxChange}
                      name={tag}
                      disabled={loadingSave}
                    />
                  }
                  label={getTwinName(tag)}
                />
              ))
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                Currently, this device has no available UI functions.
              </Typography>
            )}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loadingSave}
          variant="outlined"
          sx={{ textTransform: "none", boxShadow: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={
            !selectedItem ||
            loadingSave ||
            (selectedItem?.type === "device" &&
              !Object.values(selectedOptions).some((v) => v))
          }
          sx={{ textTransform: "none", boxShadow: "none" }}
        >
          {loadingSave ? <CircularProgress size={24} /> : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpsertDeviceDialog;
